import React, { useState, useEffect } from 'react';
import { Layout, Button, Tooltip, Avatar, Table, Tag, message, Space, Input, Select } from 'antd';
import { useNavigate } from 'react-router-dom';
import { UserOutlined, LogoutOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import ObservatoryService from '../services/ObservatoryService';
import TraceTimelineModal from './TraceTimelineModal';

const { Header, Sider, Content } = Layout;
const { Option } = Select;

// Time period options for the tracing page
// Includes longer windows (90d, 180d) than the dashboard supports.
const TIME_PERIOD_OPTIONS = [
  { value: '24h',  label: 'Last 24 Hours' },
  { value: '48h',  label: 'Last 48 Hours' },
  { value: '72h',  label: 'Last 72 Hours' },
  { value: '7d',   label: 'Last 7 Days' },
  { value: '15d',  label: 'Last 15 Days' },
  { value: '30d',  label: 'Last 30 Days' },
  { value: '60d',  label: 'Last 60 Days' },
  { value: '90d',  label: 'Last 90 Days' },
  { value: '180d', label: 'Last 6 Months' },
];

// Compute startDate ISO string from a time-period token (mirrors get_time_range on backend)
const computeStartDate = (period) => {
  const now = new Date();
  if (period.endsWith('h')) {
    const hours = parseInt(period, 10);
    now.setHours(now.getHours() - hours);
  } else if (period.endsWith('d')) {
    const days = parseInt(period, 10);
    now.setDate(now.getDate() - days);
  }
  return now.toISOString();
};

/**
 * LLMTracingPage - Page for viewing LLM call traces and observability data
 * 
 * @param {Object} props
 * @param {string} props.username - Current user's username
 * @param {string} props.sessionId - Current session ID
 * @param {function} props.onLogout - Logout handler function
 */
const LLMTracingPage = ({ username, sessionId, onLogout }) => {
  const navigate = useNavigate();

  // State for traces data
  const [traces, setTraces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0
  });

  // Timeline modal state
  const [timelineModalVisible, setTimelineModalVisible] = useState(false);
  const [selectedTrace, setSelectedTrace] = useState(null);

  // Time period - independent from the dashboard's selection (separate localStorage key)
  const [timePeriod, setTimePeriod] = useState(() => {
    return localStorage.getItem('tracing_timePeriod') || '30d';
  });

  // Filter states
  const [filters, setFilters] = useState({
    agentName: null,
    agentType: null,
    executionContext: null,
    status: null
  });

  // Available filter options - now hydrated from /api/observatory/filters
  const [availableFilters, setAvailableFilters] = useState({
    agentNames: [],
    agentTypes: [],
    executionContexts: [],
    statuses: ['completed', 'failed', 'in_progress']
  });

  const handleObservatoryClick = () => {
    navigate('/observe');
  };

  // Hydrate filter dropdown options whenever the time period changes.
  // This replaces the previous "derive options from current page rows" logic,
  // which only showed values present in the first 50 traces.
  useEffect(() => {
    const loadAvailableFilters = async () => {
      try {
        const data = await ObservatoryService.getFilterOptions(timePeriod);
        setAvailableFilters(prev => ({
          ...prev,
          agentNames: data.agent_names || [],
          agentTypes: data.agent_types || [],
          executionContexts: data.execution_contexts || [],
        }));
      } catch (err) {
        console.error('Failed to load filter options:', err);
        message.error(`Failed to load filter options: ${err.message}`);
      }
    };
    loadAvailableFilters();
  }, [timePeriod]);

  // Load traces on mount and whenever any input changes
  useEffect(() => {
    loadTraces();
  }, [pagination.current, pagination.pageSize, filters, timePeriod]);

  const loadTraces = async () => {
    try {
      setLoading(true);
      
      const offset = (pagination.current - 1) * pagination.pageSize;
      
      const filterParams = {
        limit: pagination.pageSize,
        offset: offset,
        agentName: filters.agentName,
        agentType: filters.agentType,
        executionContext: filters.executionContext,
        status: filters.status,
        startDate: computeStartDate(timePeriod),
      };

      const data = await ObservatoryService.getRootTraces(filterParams);
      
      setTraces(data.traces || []);
      setTotalCount(data.total_count || 0);
      setPagination(prev => ({
        ...prev,
        total: data.total_count || 0
      }));

      // Note: filter dropdown options are now hydrated from /api/observatory/filters
      // via the timePeriod-keyed useEffect above, not from result rows.

    } catch (error) {
      console.error('Error loading traces:', error);
      message.error(`Failed to load traces: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      total: newPagination.total
    });
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
    // Reset to first page when filter changes
    setPagination(prev => ({
      ...prev,
      current: 1
    }));
  };

  const handleTimePeriodChange = (value) => {
    setTimePeriod(value);
    localStorage.setItem('tracing_timePeriod', value);
    // Also clear the currently-selected agent/type/context filters,
    // since the new period may not contain those options anymore.
    setFilters({
      agentName: null,
      agentType: null,
      executionContext: null,
      status: null
    });
    setPagination(prev => ({
      ...prev,
      current: 1
    }));
  };

  const handleRefresh = () => {
    loadTraces();
  };

  const handleTraceClick = (record) => {
    setSelectedTrace(record);
    setTimelineModalVisible(true);
  };

  const handleTimelineModalClose = () => {
    setTimelineModalVisible(false);
    setSelectedTrace(null);
  };

  // Format datetime for display
  const formatDateTime = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  // Format latency for display
  const formatLatency = (ms) => {
    if (ms === null || ms === undefined) return 'N/A';
    if (ms < 1000) return `${ms.toFixed(0)}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  // Get status tag color
  const getStatusColor = (status) => {
    const colors = {
      'completed': '#92A372',
      'failed': 'error',
      'in_progress': 'processing'
    };
    return colors[status] || 'default';
  };

  // Table columns definition
  const columns = [
    {
      title: 'Agent Name',
      dataIndex: 'agent_name',
      key: 'agent_name',
      width: 250,
      ellipsis: true,
      render: (text) => text || 'N/A'
    },
    {
      title: 'Agent Type',
      dataIndex: 'agent_type',
      key: 'agent_type',
      width: 150,
      render: (text) => text || 'N/A'
    },
    {
      title: 'Execution Context',
      dataIndex: 'execution_context',
      key: 'execution_context',
      width: 150,
      render: (text) => text || 'N/A'
    },
    {
      title: 'Method',
      dataIndex: 'method_name',
      key: 'method_name',
      width: 200,
      ellipsis: true,
      render: (text) => text || 'N/A'
    },
    {
      title: 'Start Time',
      dataIndex: 'start_time',
      key: 'start_time',
      width: 180,
      render: (text) => formatDateTime(text)
    },
    {
      title: 'Latency',
      dataIndex: 'latency_ms',
      key: 'latency_ms',
      width: 100,
      align: 'right',
      render: (ms) => formatLatency(ms)
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status ? status.toUpperCase() : 'UNKNOWN'}
        </Tag>
      )
    },
    {
      title: 'Trace ID',
      dataIndex: 'trace_id',
      key: 'trace_id',
      width: 150,
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <span style={{ 
            fontFamily: 'monospace', 
            fontSize: '12px',
            cursor: 'pointer',
            color: '#162349'
          }}>
            {text ? text.substring(0, 8) + '...' : 'N/A'}
          </span>
        </Tooltip>
      )
    }
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Fixed Thin Sidebar */}
      <Sider 
        width={60} 
        className="sidebar-gradient"
        style={{ 
          display: 'flex', 
          background: 'linear-gradient(180deg, #162349 0%, #1c97d4 100%)',  
          flexDirection: 'column',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 10
        }}
      >
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'space-between', 
          height: '100%',
          alignItems: 'center',
          padding: '20px 0'
        }}>
          {/* Top - Empty */}
          <div></div>
          
          {/* Bottom - User controls */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center' }}>
            <div id="containerDiv">
              <hr style={{ backgroundColor: 'white', color: "white", height: '10px', border: 'none' }} />
            </div>

            <Tooltip title={username} placement="right">
              <Avatar 
                style={{ 
                  backgroundColor: 'white', 
                  color: '#162349', 
                  cursor: 'pointer',
                  border: '2px solid white'
                }}
                icon={<UserOutlined />} 
              />
            </Tooltip>
            
            <Tooltip title="Logout" placement="right">
              <Button 
                type="text" 
                icon={<LogoutOutlined style={{ color: 'white' }} />} 
                onClick={onLogout}
                style={{ color: 'white' }}
              />
            </Tooltip>
          </div>
        </div>
      </Sider>
      
      <Layout style={{ marginLeft: '60px' }}>
        {/* Header with Breadcrumb */}
        <Header style={{ 
          background: '#162349', 
          padding: '0 24px',
          height: '64px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          overflow: 'hidden',
          margin: 0,
          borderBottom: 'none'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            color: 'white',
            overflow: 'hidden'
          }}>
            <span 
              onClick={handleObservatoryClick} 
              style={{ 
                color: 'white', 
                fontSize: '24px',
                fontWeight: 'bold',
                cursor: 'pointer',
                whiteSpace: 'nowrap'
              }}
            >
              Governance and Control
            </span>
            <span style={{ 
              color: 'white', 
              margin: '0 12px',
              fontSize: '24px'
            }}>/</span>
            <span style={{ 
              color: 'white', 
              fontSize: '24px',
              fontWeight: 'bold',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              LLM Tracing
            </span>
          </div>
          
          <Button 
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={loading}
            style={{
              backgroundColor: 'white',
              color: '#92A372'
            }}
          >
            Refresh
          </Button>
        </Header>
        
        {/* Main Content Area */}
        <Content style={{ 
          padding: '24px', 
          background: 'linear-gradient(to bottom, #f8f9fa 0%, #f0f2f5 33%, #ced4da 90%, #ced4da 100%)',
          margin: 0,
          minHeight: 'calc(100vh - 64px)'
        }}>
          {/* Filters Section */}
          <div style={{ 
            marginBottom: '16px',
            background: 'white',
            padding: '16px',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
          }}>
            <Space size="middle" wrap>
              <Select
                placeholder="Time Period"
                style={{ width: 180 }}
                value={timePeriod}
                onChange={handleTimePeriodChange}
              >
                {TIME_PERIOD_OPTIONS.map(opt => (
                  <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                ))}
              </Select>

              <Select
                placeholder="Agent Name"
                style={{ width: 200 }}
                allowClear
                value={filters.agentName}
                onChange={(value) => handleFilterChange('agentName', value)}
              >
                {availableFilters.agentNames.map(name => (
                  <Option key={name} value={name}>{name}</Option>
                ))}
              </Select>

              <Select
                placeholder="Agent Type"
                style={{ width: 200 }}
                allowClear
                value={filters.agentType}
                onChange={(value) => handleFilterChange('agentType', value)}
              >
                {availableFilters.agentTypes.map(type => (
                  <Option key={type} value={type}>{type}</Option>
                ))}
              </Select>

              <Select
                placeholder="Execution Context"
                style={{ width: 200 }}
                allowClear
                value={filters.executionContext}
                onChange={(value) => handleFilterChange('executionContext', value)}
              >
                {availableFilters.executionContexts.map(ctx => (
                  <Option key={ctx} value={ctx}>{ctx}</Option>
                ))}
              </Select>

              <Select
                placeholder="Status"
                style={{ width: 150 }}
                allowClear
                value={filters.status}
                onChange={(value) => handleFilterChange('status', value)}
              >
                {availableFilters.statuses.map(status => (
                  <Option key={status} value={status}>{status.toUpperCase()}</Option>
                ))}
              </Select>
            </Space>
          </div>

          {/* Traces Table */}
          <div style={{
            background: 'white',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
          }}>
            <Table
              columns={columns}
              dataSource={traces}
              loading={loading}
              pagination={{
                ...pagination,
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} traces`,
                pageSizeOptions: ['10', '25', '50', '100']
              }}
              onChange={handleTableChange}
              rowKey="span_id"
              onRow={(record) => ({
                onClick: () => handleTraceClick(record),
                style: { cursor: 'pointer' }
              })}
              scroll={{ x: 1400 }}
              size="small"
            />
          </div>
        </Content>
      </Layout>
      
      {/* Trace Timeline Modal */}
      <TraceTimelineModal
        visible={timelineModalVisible}
        trace={selectedTrace}
        onClose={handleTimelineModalClose}
      />
    </Layout>
  );
};

export default LLMTracingPage;